/**
 * Demo UI Constants
 * Defines all UI properties, styling, and configuration constants for the Interactive Demo
 */

/**
 * Sample Data Constants
 * Used for demo purposes to simulate user input and interactions
 */

export const SAMPLE_SUBJECT = 'Test'; // Use General Knowledge as subject for demo purposes

export const SAMPLE_VOCABULARY = '異なる社会'; // Use "異なる社会" (culture) as vocabulary for demo purposes

/**
 * Overlay and Backdrop Properties
 */
export const OVERLAY = {
  // Opacity of the semi-transparent backdrop (0.0 - 1.0)
  BACKDROP_OPACITY: 0.5,
  
  // Background color of the backdrop
  BACKDROP_COLOR: 'rgba(0, 0, 0, 0.5)',
  
  // Z-index for overlay layers
  BACKDROP_Z_INDEX: 998,
  TOOLTIP_Z_INDEX: 999,
  CONTROLS_Z_INDEX: 1000,
};

/**
 * Highlight Box Properties
 */
export const HIGHLIGHT = {
  // Padding around the target element (in pixels)
  PADDING: 8,
  
  // Border color of highlight box
  BORDER_COLOR: '#FFD700',
  
  // Border width in pixels
  BORDER_WIDTH: 3,
  
  // Border style (solid, dashed, dotted)
  BORDER_STYLE: 'solid',
  
  // Box shadow for emphasis
  BOX_SHADOW: '0 0 20px rgba(255, 215, 0, 0.6)',
};

/**
 * Animation and Transition Properties
 */
export const ANIMATION = {
  // Default animation duration in milliseconds
  DURATION: 300,
  
  // Easing function for transitions
  EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Pulse animation duration (for attention-drawing effects)
  PULSE_DURATION: 2000,
  
  // Fade in/out duration
  FADE_DURATION: 300,
};

/**
 * Tooltip and Instruction Box Properties
 */
export const TOOLTIP = {
  // Maximum width of tooltip content (in pixels)
  MAX_WIDTH: 320,
  
  // Min width for better visibility
  MIN_WIDTH: 250,
  
  // Padding inside tooltip
  PADDING: 16,
  
  // Distance from target element to tooltip (in pixels)
  OFFSET: 12,
  
  // Background color
  BACKGROUND_COLOR: '#FFFFFF',
  
  // Text color
  TEXT_COLOR: '#333333',
  
  // Border radius for rounded corners
  BORDER_RADIUS: 8,
  
  // Box shadow
  BOX_SHADOW: '0 4px 20px rgba(0, 0, 0, 0.15)',
};

/**
 * Button and Control Properties
 */
export const CONTROLS = {
  // Button size
  BUTTON_HEIGHT: 40,
  BUTTON_PADDING: '8px 16px',
  
  // Button styling
  BUTTON_BORDER_RADIUS: 4,
  
  // Primary button (Next, Finish)
  PRIMARY_COLOR: '#007AFF',
  PRIMARY_HOVER: '#0051D5',
  PRIMARY_TEXT: '#FFFFFF',
  
  // Secondary button (Back)
  SECONDARY_COLOR: '#E8E8E8',
  SECONDARY_HOVER: '#D0D0D0',
  SECONDARY_TEXT: '#333333',
  
  // Danger button (Close)
  DANGER_COLOR: '#FF3B30',
  DANGER_HOVER: '#E81828',
  DANGER_TEXT: '#FFFFFF',
  
  // Disabled state
  DISABLED_COLOR: '#999999',
  DISABLED_OPACITY: 0.5,
};

/**
 * Progress Bar Properties
 */
export const PROGRESS = {
  // Height of progress bar in pixels
  HEIGHT: 4,
  
  // Background color (empty/remaining)
  BACKGROUND_COLOR: '#E8E8E8',
  
  // Filled progress color
  FILL_COLOR: '#007AFF',
  
  // Border radius
  BORDER_RADIUS: 2,
};

/**
 * Typography and Text Properties
 */
export const TYPOGRAPHY = {
  // Step title font size
  TITLE_SIZE: '20px',
  TITLE_WEIGHT: 600,
  
  // Description text size
  DESCRIPTION_SIZE: '14px',
  DESCRIPTION_WEIGHT: 400,
  DESCRIPTION_COLOR: '#666666',
  
  // Instruction text size
  INSTRUCTION_SIZE: '14px',
  INSTRUCTION_WEIGHT: 500,
  INSTRUCTION_COLOR: '#333333',
  
  // Step counter/info text
  INFO_SIZE: '12px',
  INFO_WEIGHT: 400,
  INFO_COLOR: '#999999',
};

/**
 * Spacing and Layout Properties
 */
export const SPACING = {
  // Padding for containers
  CONTAINER_PADDING: 16,
  
  // Gap between elements
  ELEMENT_GAP: 12,
  
  // Margin between sections
  SECTION_MARGIN: 20,
};

/**
 * Demo State and Behavior Constants
 */
export const DEMO_BEHAVIOR = {
  // Maximum time to wait for element to appear in DOM (milliseconds)
  ELEMENT_WAIT_TIMEOUT: 5000,
  
  // Auto-scroll behavior
  AUTO_SCROLL: true,
  SCROLL_PADDING: 100, // pixels from top/bottom
  
  // Show congratulation screen at the end
  SHOW_COMPLETION_SCREEN: true,
  
  // Allow user to skip steps
  ALLOW_SKIP: true,
  
  // Allow user to restart demo
  ALLOW_RESTART: true,
  
  // Persist demo state in localStorage
  PERSIST_STATE: true,
  
  // localStorage key for demo completion status
  STORAGE_KEY_COMPLETED: 'demo_completed',
  
  // localStorage key for demo progress
  STORAGE_KEY_PROGRESS: 'demo_progress',
  
  // localStorage key for user interactions
  STORAGE_KEY_INTERACTIONS: 'demo_interactions',
};

/**
 * Accessibility Constants
 */
export const ACCESSIBILITY = {
  // Enable keyboard navigation (arrow keys, Esc)
  KEYBOARD_NAVIGATION: true,
  
  // Enable focus management
  FOCUS_MANAGEMENT: true,
  
  // Ensure sufficient color contrast
  MIN_CONTRAST_RATIO: 4.5,
  
  // Announce step changes to screen readers
  ANNOUNCE_CHANGES: true,
};

/**
 * Tooltip Position Options
 */
export const POSITION_OPTIONS = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  AUTO: 'auto', // Automatically choose best position
};

/**
 * Action Types
 */
export const ACTION_TYPES = {
  CLICK: 'click',
  SCROLL: 'scroll',
  VIEW: 'view',
  INPUT: 'input',
};

/**
 * Demo Status
 */
export const DEMO_STATUS = {
  IDLE: 'idle',
  ACTIVE: 'active',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  CLOSED: 'closed',
};

/**
 * Responsive Breakpoints (pixels)
 */
export const BREAKPOINTS = {
  MOBILE: 480,
  TABLET: 768,
  DESKTOP: 1024,
  LARGE: 1440,
};

/**
 * Mobile-specific adjustments
 */
export const MOBILE_ADJUSTMENTS = {
  // Reduce tooltip max width on mobile
  TOOLTIP_MAX_WIDTH_MOBILE: 280,
  
  // Adjust padding for smaller screens
  CONTAINER_PADDING_MOBILE: 12,
  
  // Adjust button sizes
  BUTTON_HEIGHT_MOBILE: 36,
};

/**
 * Synchronization Events
 * Events emitted by the synchronization service
 */
export const SYNC_EVENTS = {
  // When synchronization is initialized
  SYNC_INITIALIZED: 'sync:initialized',

  // When user interaction is detected
  INTERACTION_DETECTED: 'sync:interaction-detected',

  // When out-of-scope interaction is detected
  OUT_OF_SCOPE_INTERACTION: 'sync:out-of-scope-interaction',

  // When step is advanced
  STEP_ADVANCED: 'sync:step-advanced',

  // When going to previous step
  STEP_PREVIOUS: 'sync:step-previous',

  // When demo is completed
  DEMO_COMPLETED: 'sync:demo-completed',

  // When synchronization stops
  SYNC_STOPPED: 'sync:stopped',

  // When an error occurs
  SYNC_ERROR: 'sync:error',
};

/**
 * Synchronization Configuration
 * Configuration parameters for synchronization behavior
 */
export const SYNC_CONFIG = {
  // Timeout for detecting target element (milliseconds)
  // Increased to 8s to accommodate API calls on page load
  ELEMENT_DETECTION_TIMEOUT: 8000,

  // Number of retries for element detection
  MAX_ELEMENT_RETRIES: 5,

  // Delay between retries (milliseconds)
  ELEMENT_RETRY_DELAY: 500,

  // Debounce delay for interaction events (milliseconds)
  DEBOUNCE_DELAY: 300,

  // Debounce delay for scroll events (milliseconds)
  SCROLL_DEBOUNCE_DELAY: 300,

  // Debounce delay for focus events (milliseconds)
  FOCUS_DEBOUNCE_DELAY: 100,

  // Name of localStorage key for synchronization progress
  STORAGE_KEY_SYNC_PROGRESS: 'north_star_demo_sync_progress',

  // Name of localStorage key for navigation history
  STORAGE_KEY_NAV_HISTORY: 'north_star_demo_nav_history',

  // Delay before advancing to the next step after an interaction (milliseconds)
  WAIT_BEFORE_NEXT_STEP: 300,
};

export default {
  OVERLAY,
  HIGHLIGHT,
  ANIMATION,
  TOOLTIP,
  CONTROLS,
  PROGRESS,
  TYPOGRAPHY,
  SPACING,
  DEMO_BEHAVIOR,
  ACCESSIBILITY,
  POSITION_OPTIONS,
  ACTION_TYPES,
  DEMO_STATUS,
  BREAKPOINTS,
  MOBILE_ADJUSTMENTS,
  SYNC_EVENTS,
  SYNC_CONFIG,
};

// ─── Phase 3: UI Controls & Confirmation Dialog ───────────────────────────────

export const DEMO_CONTROLS_CONFIG = {
  BACK_BUTTON: {
    label: 'Back',
    ariaLabel: 'Go to previous step',
    title: 'Go to previous step (Page Up)',
    icon: 'arrow-back',
    shortcut: 'Page Up',
  },
  NEXT_BUTTON: {
    label: 'Next',
    ariaLabel: 'Go to next step',
    title: 'Go to next step (Page Down)',
    icon: 'arrow-forward',
    shortcut: 'Page Down',
    alwaysEnabled: true,
  },
  CLOSE_BUTTON: {
    label: 'Close',
    ariaLabel: 'Close demo',
    title: 'Close demo (Esc)',
    icon: 'close',
    shortcut: 'Escape',
  },
  FINISH_BUTTON: {
    label: 'Finish',
    ariaLabel: 'Complete demo',
    title: 'Complete demo and exit',
    icon: 'check',
  },
  KEYBOARD_SHORTCUTS: {
    PREVIOUS: ['PageUp', 'ArrowUp'],
    NEXT: ['PageDown', 'ArrowDown'],
    CLOSE: ['Escape'],
  },
};

export const DEMO_CONFIRMATION_CONFIG = {
  DIALOG: {
    width: '400px',
    maxWidth: '90vw',
    position: 'center',
  },
  TITLE: 'Continue Demo?',
  MESSAGES: {
    OUT_OF_SCOPE: 'You performed an action outside the guided step. Would you like to continue with the demo or proceed manually?',
    INVALID_INTERACTION: 'This interaction is not part of the current step. Please return to the highlighted area.',
    NAVIGATION_WARNING: 'Navigating away will exit the demo. Continue?',
  },
  BUTTONS: {
    CONTINUE: {
      label: 'Continue Demo',
      variant: 'primary',
      icon: 'check',
    },
    EXIT: {
      label: 'Exit & Proceed',
      variant: 'secondary',
      icon: 'close',
    },
    CANCEL: {
      label: 'Cancel',
      variant: 'tertiary',
      icon: 'times',
    },
  },
  SHOW_DONT_ASK_AGAIN: true,
  STORAGE_KEY_DONT_ASK: 'north_star_demo_dont_ask_confirmation',
};

export const BUTTON_STATES = {
  NEXT_ALWAYS_ENABLED: true,
  PREVIOUS_DISABLED_ON_FIRST: true,
  SHOW_CLOSE_ON_FINAL: false,
  SHOW_FINISH_ON_FINAL: true,
  TRANSITION_DURATION: 200,
};

export const DEMO_CONTROLS_ACCESSIBILITY = {
  ENABLE_KEYBOARD_SHORTCUTS: true,
  ANNOUNCE_NAVIGATION: true,
  FOCUS_ON_CHANGE: true,
  ARIA_LIVE_POLITE: true,
  TRAP_FOCUS: false,
};

export const DEMO_CONTROLS_ERRORS = {
  CANNOT_GO_BACK: 'You are at the first step. Cannot navigate backward.',
  CANNOT_GO_FORWARD: 'You have reached the final step.',
  ELEMENT_NOT_FOUND: 'Could not locate the target element for this step.',
  NAVIGATION_FAILED: 'Failed to navigate. Please try again.',
  SYNC_ERROR: 'Demo synchronization error. Please refresh and try again.',
};

export const DEMO_CONTROLS_ANIMATION = {
  BUTTON_HOVER_SCALE: 1.05,
  BUTTON_ACTIVE_SCALE: 0.98,
  TRANSITION_EASING: 'cubic-bezier(0.4, 0, 0.2, 1)',
  DIALOG_FADE_DURATION: 300,
};

// ─── Phase 5: Multi-Page Navigation & Route Integration ───────────────────────

export const ROUTE_NAVIGATION_CONFIG = {
  AUTO_NAVIGATE_ON_STEP_CHANGE: true,
  ALLOW_MANUAL_ROUTE_CHANGE: false,
  VALIDATE_ELEMENT_BEFORE_NAV: true,
  NAVIGATION_METHOD: 'router-push',
  USE_HASH_NAVIGATION: false,
  TRANSITION_DURATION: 300,
  PRESERVE_SCROLL: false,
  REPLACE_HISTORY: false,
  LOG_NAVIGATION: false,
  USE_QUERY_PARAMS: false,
};

export const ROUTE_GUARD_CONFIG = {
  ENABLE_GUARDS: true,
  GUARD_TYPE: 'both',
  STRICT_ROUTE_VALIDATION: true,
  ALLOWED_ROUTES: [],
  SHOW_CONFIRMATION: true,
  CONFIRMATION_MESSAGE: 'Navigating away will exit the demo. Continue?',
  ALLOW_SAME_ROUTE_NAV: true,
  ALLOW_PARENT_ROUTE_NAV: false,
  BLOCK_EXTERNAL_NAV: true,
  EXTERNAL_LINK_PATTERNS: [
    /^https?:\/\/(?!.*current-domain)/,
  ],
  LOG_GUARDS: false,
  GUARD_TIMEOUT: 5000,
};

export const ROUTE_TRANSITION_CONFIG = {
  WAIT_FOR_TRANSITION: true,
  DOM_READY_TIMEOUT: 3000,
  WAIT_FOR_ELEMENTS: true,
  MAX_NAV_RETRIES: 5,
  NAV_RETRY_DELAY: 300,
  CHECK_ROUTER_READY: true,
  WAIT_FOR_COMPONENT_MOUNT: true,
  COMPONENT_MOUNT_TIMEOUT: 3000,
  USE_ROUTE_COMPOSABLE: true,
  SCROLL_BEHAVIOR: 'top',
  ENABLE_ANIMATIONS: true,
  LOG_TRANSITIONS: false,
  TRACK_TRANSITIONS: true,
  MAX_TRANSITION_HISTORY: 20,
};

export const STATE_PRESERVATION_CONFIG = {
  ENABLE_STATE_PRESERVATION: true,
  USE_LOCAL_STORAGE: true,
  USE_SESSION_STORAGE: true,
  STATE_STORAGE_KEY: 'north_star_demo_state',
  SESSION_STATE_KEY: 'north_star_demo_session',
  AUTO_RESTORE_STATE: true,
  PRESERVE_ON_STEP_CHANGE: true,
  PRESERVE_ON_ROUTE_CHANGE: true,
  SNAPSHOT_INTERVAL: 1000,
  MAX_STATE_SNAPSHOTS: 10,
  ENCRYPT_STATE: false,
  STATE_PROPERTIES: [
    'currentStepIndex',
    'demoSteps',
    'currentRoute',
    'startTime',
    'completedSteps',
    'userInteractions',
  ],
  EXCLUDE_PROPERTIES: ['activeListeners', 'debounceTimer'],
  LOG_STATE_CHANGES: false,
  STATE_VERSION: '1.0',
};

export const ROUTE_FALLBACK_CONFIG = {
  ENABLE_FALLBACK: true,
  AUTO_RETURN_ON_ERROR: true,
  FALLBACK_TO_FIRST_STEP: false,
  FALLBACK_HOME_ROUTE: '/',
  SKIP_ON_NAV_FAILURE: false,
  MAX_FALLBACK_ATTEMPTS: 3,
  FALLBACK_RETRY_DELAY: 500,
  LOG_FALLBACKS: true,
  SAFE_FALLBACK_ROUTES: ['/', '/home', '/dashboard', '/help'],
  BLOCKED_ROUTES: ['/admin', '/settings', '/logout'],
  DEFAULT_FALLBACK_ACTION: 'return-previous',
  RECOVERY_TIMEOUT: 5000,
};

export const ROUTE_DETECTION_CONFIG = {
  ENABLE_ROUTE_DETECTION: true,
  MONITOR_ROUTE_CHANGES: true,
  CASE_SENSITIVE: false,
  EXACT_MATCH: false,
  IGNORE_QUERY_PARAMS: true,
  IGNORE_HASH: true,
  NORMALIZE_ROUTES: true,
  MATCH_TIMEOUT: 100,
  LOG_DETECTION: false,
  DETECTION_INTERVAL: 500,
  MAX_DETECTION_ATTEMPTS: 10,
};

export const MULTI_PAGE_FLOW_CONFIG = {
  ENABLE_MULTI_PAGE: true,
  GROUP_STEPS_BY_ROUTE: true,
  AUTO_ADVANCE_ROUTES: true,
  WARN_INCOMPLETE_STEP: true,
  FORCE_STEP_COMPLETION: false,
  ALLOW_STEP_SKIP: true,
  TRACK_ROUTE_VISITS: true,
  MAX_ROUTE_VISITS: null,
  WARN_EXCESSIVE_VISITS: true,
  SHOW_ROUTE_PROGRESS: true,
  LOG_FLOW_EVENTS: false,
};

export const ROUTE_METADATA_CONFIG = {
  ENABLE_METADATA: true,
  METADATA_PROPERTY: 'demoMeta',
  DEMO_CONTEXT_ROUTES: [],
  EXCLUDED_ROUTES: ['/login', '/register', '/forgot-password'],
  SPECIAL_ROUTES: {},
  DEFAULT_METADATA: {
    isDemoRoute: false,
    demoSteps: [],
    demoRequired: false,
  },
  LOG_METADATA: false,
};

export const ROUTE_NAVIGATION_ERRORS = {
  ROUTE_NOT_FOUND: {
    code: 'ROUTE_NOT_FOUND',
    message: 'Target route not found',
    recoverable: true,
    fallbackAction: 'return-home',
  },
  NAVIGATION_BLOCKED: {
    code: 'NAVIGATION_BLOCKED',
    message: 'Navigation blocked by demo guard',
    recoverable: true,
    fallbackAction: 'show-confirmation',
  },
  TRANSITION_TIMEOUT: {
    code: 'TRANSITION_TIMEOUT',
    message: 'Route transition took too long',
    recoverable: true,
    fallbackAction: 'retry',
  },
  ELEMENT_NOT_FOUND_AFTER_NAV: {
    code: 'ELEMENT_NOT_FOUND_AFTER_NAV',
    message: 'Target element not found in route',
    recoverable: true,
    fallbackAction: 'skip-step',
  },
  COMPONENT_NOT_MOUNTED: {
    code: 'COMPONENT_NOT_MOUNTED',
    message: 'Route component did not mount',
    recoverable: true,
    fallbackAction: 'retry',
  },
  INVALID_ROUTE_FOR_STEP: {
    code: 'INVALID_ROUTE_FOR_STEP',
    message: 'Current route does not match step route',
    recoverable: true,
    fallbackAction: 'navigate',
  },
};

// ─── Phase 6: State Management & Persistence ─────────────────────────────────

export const STORAGE_CONFIG = {
  PRIMARY_STORAGE: 'localStorage',
  FALLBACK_STORAGE: 'sessionStorage',
  STATE_KEY: 'demo_state',
  HISTORY_KEY: 'demo_history',
  PROGRESS_KEY: 'demo_progress',
  INTERACTIONS_KEY: 'demo_interactions',
  COMPLETION_KEY: 'demo_completion',
  STORAGE_PREFIX: 'northstar_',
  COMPRESS_HISTORY: true,
  MAX_STORAGE_SIZE_KB: 5000,
  AUTO_CLEANUP_ENABLED: true,
  AUTO_CLEANUP_INTERVAL_MS: 3600000,
};

export const STATE_PERSISTENCE_CONFIG = {
  PERSIST_CURRENT_STEP: true,
  PERSIST_STARTED_TIME: true,
  PERSIST_LAST_ACTIVE_TIME: true,
  PERSIST_TOTAL_DURATION: true,
  PERSIST_COMPLETED_STEPS: true,
  PERSIST_SKIPPED_STEPS: true,
  PERSIST_IS_COMPLETED: true,
  PERSIST_DEMO_STATUS: true,
  PERSIST_CURRENT_ROUTE: true,
  PERSIST_ON_STEP_CHANGE: true,
  PERSIST_ON_INTERACTION: false,
  PERSIST_PERIODICALLY: true,
  PERSIST_PERIODIC_INTERVAL_MS: 30000,
  PERSIST_ON_VISIBILITY_CHANGE: true,
  PERSIST_ON_BEFORE_UNLOAD: true,
  AUTO_SAVE_THRESHOLD: 5,
  STATE_EXPIRATION_DAYS: 30,
};

export const HISTORY_TRACKING_CONFIG = {
  TRACKING_ENABLED: true,
  TRACK_STEP_CHANGES: true,
  TRACK_NAVIGATION: true,
  TRACK_INTERACTIONS: true,
  TRACK_ERRORS: true,
  TRACK_USER_ACTIONS: true,
  TRACK_ROUTE_CHANGES: true,
  TRACK_PAUSE_RESUME: true,
  MAX_HISTORY_ENTRIES: 100,
  MAX_INTERACTION_ENTRIES: 50,
  HISTORY_TIME_WINDOW_HOURS: 24,
  ENTRY_FIELDS: {
    timestamp: true,
    action: true,
    stepIndex: true,
    stepId: true,
    eventType: true,
    context: true,
    userAction: true,
    route: true,
    metadata: true,
  },
  COMPRESS_OLD_ENTRIES: true,
  COMPRESSION_AGE_HOURS: 12,
  CLEAR_ON_COMPLETION: false,
  AUTO_ARCHIVE_ENABLED: true,
  AUTO_ARCHIVE_AGE_DAYS: 7,
};

export const PROGRESS_TRACKING_CONFIG = {
  TRACKING_ENABLED: true,
  PROGRESS_METRICS: {
    currentStepIndex: true,
    completionPercentage: true,
    stepsCompleted: true,
    stepsSkipped: true,
    stepsRemaining: true,
    estimatedTimeRemaining: true,
  },
  ENABLE_MILESTONES: true,
  MILESTONE_INTERVALS: [25, 50, 75, 100],
  TRACK_SESSION_DURATION: true,
  TRACK_STEP_DURATION: true,
  CALCULATE_AVERAGE_STEP_TIME: true,
  ENABLE_TIME_ESTIMATION: true,
  BASE_STEP_DURATION_MS: 30000,
  PERSIST_ON_STEP_COMPLETE: true,
  AUTO_RESET_ON_NEW_SESSION: false,
  RESET_ON_EXPLICIT_REQUEST: true,
};

export const COMPLETION_TRACKING_CONFIG = {
  MARK_COMPLETE_ON_FINAL_STEP: true,
  MARK_COMPLETE_ON_EXPLICIT_END: true,
  COMPLETION_STATUS_OPTIONS: {
    NOT_STARTED: 'not-started',
    IN_PROGRESS: 'in-progress',
    PAUSED: 'paused',
    COMPLETED: 'completed',
    ABANDONED: 'abandoned',
    SKIPPED: 'skipped',
  },
  INACTIVITY_THRESHOLD_MINUTES: 60,
  SHOW_COMPLETION_MESSAGE: true,
  ALLOW_RESTART_AFTER_COMPLETION: true,
  PERSIST_COMPLETION_STATUS: true,
  TRACK_COMPLETION_COUNT: true,
};

export const STATE_RECOVERY_CONFIG = {
  AUTO_RECOVERY_ENABLED: true,
  RECOVERY_STRATEGIES: [
    'RESTORE_LAST_SESSION',
    'RESTORE_LATEST_CHECKPOINT',
    'RESTORE_FIRST_INCOMPLETE',
    'START_FROM_BEGINNING',
  ],
  SHOW_RECOVERY_PROMPT: true,
  RECOVERY_PROMPT_TIMEOUT_MS: 5000,
  RECOVERY_DECISION_TIMEOUT_MS: 30000,
  AUTO_RECOVERY_NO_PROMPT: false,
  AUTO_RECOVERY_THRESHOLD_PERCENT: 10,
  VALIDATE_RECOVERED_STATE: true,
  VALIDATE_STEP_ACCESSIBILITY: true,
  VALIDATE_ROUTE_EXISTENCE: true,
  FALLBACK_ON_RECOVERY_FAILURE: true,
  FALLBACK_ACTION: 'START_FROM_BEGINNING',
};

export const STORAGE_HEALTH_CONFIG = {
  ENABLE_STORAGE_MONITORING: true,
  MONITOR_STORAGE_INTERVAL_MS: 600000,
  WARN_AT_STORAGE_PERCENT: 80,
  CLEANUP_ENABLED: true,
  CLEANUP_INTERVAL_MS: 3600000,
  CLEANUP_EXPIRED_STATES: true,
  CLEANUP_ABANDONED_DEMOS: true,
  CLEANUP_OLD_HISTORY: true,
  CLEANUP_CORRUPT_DATA: true,
  CLEANUP_STATES_OLDER_THAN_DAYS: 30,
  CLEANUP_ABANDONED_AFTER_DAYS: 7,
  CLEANUP_HISTORY_OLDER_THAN_DAYS: 7,
  CREATE_BACKUP_BEFORE_CLEANUP: true,
  MAX_BACKUPS_TO_KEEP: 3,
  LOG_CLEANUP_ACTIONS: false,
};

export const STATE_EXPORT_CONFIG = {
  EXPORT_ENABLED: true,
  EXPORT_CURRENT_STATE: true,
  EXPORT_HISTORY: true,
  EXPORT_PROGRESS: true,
  EXPORT_COMPLETION_DATA: true,
  EXPORT_TIMESTAMPS: true,
  EXPORT_ANALYTICS: true,
  EXPORT_FORMAT: 'json',
  PRETTY_PRINT_JSON: true,
  INCLUDE_METADATA: true,
  IMPORT_VALIDATION_ENABLED: true,
  VALIDATE_SCHEMA: true,
  VALIDATE_DATA_TYPES: true,
  VALIDATE_TIMESTAMPS: true,
};

export const STATE_SYNC_CONFIG = {
  SYNC_ENABLED: true,
  LISTEN_TO_STORAGE_EVENTS: true,
  BROADCAST_STATE_CHANGES: true,
  BROADCAST_INTERVAL_MS: 5000,
  CONFLICT_RESOLUTION_STRATEGY: 'LATEST_TIMESTAMP',
  SYNC_ON_VISIBILITY_CHANGE: true,
};

export const STATE_MANAGEMENT_ERRORS = {
  STORAGE_UNAVAILABLE: {
    code: 'STATE_001',
    message: 'Storage mechanism unavailable',
    severity: 'WARNING',
    recovery: 'FALLBACK_TO_MEMORY',
  },
  STORAGE_QUOTA_EXCEEDED: {
    code: 'STATE_002',
    message: 'Storage quota exceeded',
    severity: 'WARNING',
    recovery: 'CLEANUP_OLD_DATA',
  },
  CORRUPT_STATE_DATA: {
    code: 'STATE_003',
    message: 'Stored state data is corrupted',
    severity: 'WARNING',
    recovery: 'USE_RECOVERY_STRATEGY',
  },
  INVALID_STATE_FORMAT: {
    code: 'STATE_004',
    message: 'State data format is invalid',
    severity: 'WARNING',
    recovery: 'RESET_TO_DEFAULT',
  },
  RECOVERY_FAILED: {
    code: 'STATE_005',
    message: 'State recovery unsuccessful',
    severity: 'ERROR',
    recovery: 'START_FROM_BEGINNING',
  },
  HISTORY_SYNC_FAILED: {
    code: 'STATE_006',
    message: 'Failed to sync history across tabs',
    severity: 'INFO',
    recovery: 'CONTINUE_LOCAL',
  },
  EXPORT_FAILED: {
    code: 'STATE_007',
    message: 'State export failed',
    severity: 'WARNING',
    recovery: 'RETRY_EXPORT',
  },
  IMPORT_FAILED: {
    code: 'STATE_008',
    message: 'State import failed validation',
    severity: 'ERROR',
    recovery: 'REJECT_IMPORT',
  },
};

export const STATE_DEBUG_CONFIG = {
  DEBUG_ENABLED: process.env.NODE_ENV === 'development',
  LOG_STATE_CHANGES: true,
  LOG_STORAGE_OPERATIONS: true,
  LOG_RECOVERY_PROCESS: true,
  LOG_HISTORY_OPERATIONS: true,
  LOG_CLEANUP_ACTIONS: false,
  SHOW_STORAGE_STATUS: false,
  SHOW_RECOVERY_PROMPT: true,
  TRACK_STORAGE_PERFORMANCE: true,
  WARN_ON_SLOW_STORAGE: true,
  SLOW_STORAGE_THRESHOLD_MS: 50,
};
